import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareBar from "@/components/feature/ShareBar";
import ArticleRow from "@/components/feature/ArticleRow";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { articles, formatViews, getArticleById, getRelatedArticles } from "@/lib/media";

const categoryStyles: Record<string, string> = {
  新闻: "bg-primary-100 text-primary-700",
  影评: "bg-accent-100 text-accent-700",
  专题: "bg-secondary-100 text-secondary-700",
  预告: "bg-background-200 text-foreground-700",
};

const sorted = [...articles].sort((a, b) => b.date.localeCompare(a.date));

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id ?? "");
  const index = article ? sorted.findIndex((item) => item.id === article.id) : -1;
  const prev = index > 0 ? sorted[index - 1] : null;
  const next = index >= 0 && index < sorted.length - 1 ? sorted[index + 1] : null;

  useDocumentMeta({
    title: article ? `${article.title} - 色虎影视资讯` : "影视资讯详情 - 色虎",
    description: article
      ? `${article.summary}（${article.category} · ${article.date} · ${article.author}）`
      : "色虎影视资讯详情页，提供最新影视行业报道与深度影评。",
    keywords: article ? `色虎资讯,${article.category},${article.tags.join(",")}` : "色虎影视资讯,影评,专题",
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="portal-container py-16">
          <EmptyState
            icon="ri-article-line"
            title="这篇资讯不存在"
            description="链接可能已经失效，返回资讯列表查看最新内容。"
            actionLabel="返回资讯列表"
            onAction={() => window.REACT_APP_NAVIGATE("/news")}
          />
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedArticles(article, 4);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="portal-container py-6">
          <Breadcrumb items={[{ label: "影视资讯", to: "/news" }, { label: article.category }]} />
          <div className="mt-4 max-w-4xl">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded px-2 py-1 font-medium ${categoryStyles[article.category]}`}>{article.category}</span>
              <span className="text-foreground-400">{article.date}</span>
              <span className="text-foreground-400">{article.author}</span>
              <span className="flex items-center gap-1 text-foreground-400">
                <i className="ri-eye-line" aria-hidden="true" />
                {formatViews(article.views)} 阅读
              </span>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl md:leading-snug">
              {article.title}
            </h1>
            <p className="mt-3 text-sm leading-7 text-foreground-600">{article.summary}</p>
            <div className="mt-4">
              <ShareBar title={article.title} />
            </div>
          </div>
        </div>
      </div>

      <div className="portal-container mt-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="min-w-0">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} 影视资讯 色虎`}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="px-5 py-6 md:px-8 md:py-8">
                <div className="prose-article text-[15px] text-foreground-700">
                  {article.content.map((block, blockIndex) => {
                    const key = `${article.id}-${blockIndex}`;
                    if (block.type === "h2") {
                      return (
                        <h2 key={key} className="mb-3 mt-8 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.type === "quote") {
                      return (
                        <blockquote
                          key={key}
                          className="my-6 border-l-2 border-accent-500 bg-background-100 px-4 py-3 text-sm leading-8 text-foreground-700"
                        >
                          {block.text}
                        </blockquote>
                      );
                    }
                    if (block.type === "image") {
                      return (
                        <figure key={key} className="my-6">
                          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
                            <img src={block.src} alt={block.caption} className="h-full w-full object-cover" />
                          </div>
                          <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>
                        </figure>
                      );
                    }
                    return (
                      <p key={key} className="text-[15px] leading-8 text-foreground-700">
                        {block.text}
                      </p>
                    );
                  })}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
                  <span className="text-xs text-foreground-400">标签</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:text-accent-600"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-500">觉得这篇内容有用？分享给同样喜欢影视的朋友</p>
                  <ShareBar title={article.title} />
                </div>
                <p className="mt-3 text-[11px] text-foreground-400">
                  <i className="ri-information-line mr-1" aria-hidden="true" />
                  本文为演示内容，人物、数据与事件均为示例，仅用于展示资讯阅读页面。
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {prev ? (
                <Link
                  to={`/news/detail/${prev.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <p className="flex items-center gap-1 text-[11px] text-foreground-400">
                    <i className="ri-arrow-left-line" aria-hidden="true" />
                    上一篇
                  </p>
                  <p className="mt-1.5 line-clamp-2 text-sm text-foreground-800 group-hover:text-accent-600">{prev.title}</p>
                </Link>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 p-4 text-xs text-foreground-400">
                  已经是第一篇资讯
                </div>
              )}
              {next ? (
                <Link
                  to={`/news/detail/${next.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 text-right transition-colors hover:border-primary-300"
                >
                  <p className="flex items-center justify-end gap-1 text-[11px] text-foreground-400">
                    下一篇
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </p>
                  <p className="mt-1.5 line-clamp-2 text-sm text-foreground-800 group-hover:text-accent-600">{next.title}</p>
                </Link>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 p-4 text-right text-xs text-foreground-400">
                  已经是最新一篇资讯
                </div>
              )}
            </div>
          </article>

          <aside className="space-y-6">
            <section className="card-surface overflow-hidden">
              <div className="flex items-center justify-between border-b border-background-200 bg-primary-500 px-4 py-3">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-background-50">
                  <i className="ri-book-open-line" aria-hidden="true" />
                  相关文章
                </h2>
                <Link to="/news" className="text-[11px] text-primary-200 hover:text-accent-300">
                  全部
                </Link>
              </div>
              <div className="px-4">
                {related.map((item) => (
                  <ArticleRow key={item.id} article={item} variant="compact" />
                ))}
              </div>
            </section>

            <section className="card-surface p-4">
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-sm font-semibold text-foreground-950">
                <i className="ri-links-line text-accent-500" aria-hidden="true" />
                继续探索
              </h2>
              <div className="mt-3 space-y-2 text-sm">
                <Link
                  to="/category/movie"
                  className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  浏览电影分类
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                </Link>
                <Link
                  to="/category/tv"
                  className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  浏览电视剧分类
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                </Link>
                <Link
                  to={`/search?q=${encodeURIComponent(article.tags[0] ?? "影视")}`}
                  className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  搜索「{article.tags[0] ?? "影视"}」
                  <i className="ri-search-line" aria-hidden="true" />
                </Link>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}